/**
 * "What agreements exist here?" — the rollup section shared by the Hotels and
 * Customers detail panels.
 *
 * The two panels ask the same question from opposite ends: a hotel's agreements
 * span CUSTOMERS, a customer's span PROPERTIES. So the section takes a `show`
 * prop naming which side to put in the first column, and everything else — the
 * status badges, the term/rate columns, the capped-list footer — is identical.
 *
 * Types mirror the API's src/lib/hsp/agreement-rollup.ts. `total` is the true
 * count and `shown` is capped server-side, which is why `truncated` exists: a
 * property with forty agreements must not read as a property with eight.
 */
import { Link } from 'react-router';
import { Group } from '@/features/crm/components/DetailPanel';
import { fmtMaybeCalendarDate } from '@/features/crm/lib/dates';
import { statusBadge, statusLabel } from '../agreement-status';

export interface AgreementRac {
  rac: string;
  gds: string | null;
  target: string;
}

export interface AgreementSummary {
  id: string;
  status: string;
  /** Postgres DATE as ISO — format as a calendar date, never in local time. */
  termEnd: string | null;
  /** Decimal as a string so no precision was lost in transit. */
  rateAmount: string | null;
  rateCurrency: string | null;
  /** public | negotiated | consortia. Null when there is no rate yet. */
  rateType?: string | null;
  /** Access codes this agreement was LOADED under — see the API lib. */
  racs?: AgreementRac[];
  customerId: string;
  customerName: string | null;
  propertyName: string | null;
  /**
   * Notes written on THIS agreement, surfaced at the PROPERTY so they roll up.
   *
   * Stacie, 2026-08-31: "we need to make sure that we have agreement level
   * notes and hotel level notes. And agreement level notes should show up when
   * I click on the hotel" — and, when asked whether it went both ways, "but not
   * the other way around."
   *
   * So the direction is one-way on purpose. A note about one customer's
   * negotiation is context for anyone else working that property; a property
   * fact is not automatically true of every agreement on it.
   */
  internalNote?: string | null;
  hotelNote?: string | null;
}

export interface AgreementRollup {
  total: number;
  shown: AgreementSummary[];
  /** total - shown.length. Non-zero means the list was capped server-side. */
  truncated: number;
}

/*
 * There is deliberately NO exported EMPTY_ROLLUP constant.
 *
 * One existed, and its only two call sites both used it to paper over a rollup
 * that was not known yet — `agreements ?? EMPTY_ROLLUP` — which is exactly how
 * the panel came to claim "nothing has been sourced" over a failed fetch. Pass
 * `undefined` for "not known"; a real empty rollup only ever comes from the API.
 */

function rate(a: AgreementSummary): string {
  if (a.rateAmount == null) return '—';
  // Whole units, matching the Agreements table's Rate column — the cents are in
  // the agreement's own detail, not in a scan-and-compare list.
  return `${a.rateCurrency ?? ''} ${Number(a.rateAmount).toFixed(0)}`.trim();
}

/**
 * The access codes for one agreement, as "0FR (Sabre)".
 *
 * Plural on purpose — an agreement loads into the GDS and the online booking tool
 * separately and each carries its own code. The GDS is named because the code
 * alone is ambiguous: the same three characters mean different things in Sabre
 * and Travelport, and reading one out to the wrong desk is a real failure.
 */
function racList(a: AgreementSummary): string | null {
  const racs = a.racs ?? [];
  if (racs.length === 0) return null;
  return racs.map((r) => (r.gds ? `${r.rac} (${r.gds})` : r.rac)).join(', ');
}

export function AgreementRollupSection({
  emptyMessage,
  moreHref,
  rollup,
  show,
  testId,
}: {
  /** Shown when there are no agreements at all — say why, not just "none". */
  emptyMessage: string;
  /**
   * Where "+N more" points. OPTIONAL because it depends on what the agreements
   * endpoint can actually filter by: it accepts `?customer_id=`, so the
   * Customers panel links; it has no hotel filter, so the Hotels panel states
   * the count without a link rather than offering one that would silently show
   * every agreement in the book.
   */
  moreHref?: string;
  /**
   * The rollup, or `undefined` when it is NOT KNOWN — still in flight, or the
   * request failed. Those are different from an empty rollup and must not render
   * as one; see below.
   */
  rollup: AgreementRollup | undefined;
  /** Which side of the join to name in the first column. */
  show: 'customer' | 'property';
  testId?: string;
}) {
  /*
   * THREE states, not two. Raised by Josh reviewing #1742.
   *
   * The Customers panel fetches its rollup in a second request whose failure is
   * swallowed on purpose (the customer list is fully usable without it). Before
   * this, a failed or in-flight rollup fell back to an empty one and the panel
   * stated "nothing has been sourced against their program" — a definitive claim
   * about the data, made when we do not have the data.
   *
   * That is the same class of error as a capped list reading as a complete one,
   * which this component already refuses to commit elsewhere. "We do not know"
   * has to be sayable.
   *
   * In-flight and failed are deliberately collapsed into one state: the wording
   * is true of both, and the panel is only ever read after a human click, by
   * which time the request has almost always resolved. Distinguishing them would
   * add a prop and a spinner to buy accuracy about a state nobody sees.
   */
  if (rollup == null) {
    return (
      <Group label="Agreements" testId={testId}>
        <p className="detail-note" data-testid={`${testId}-unavailable`}>
          <span className="muted">
            Agreements aren&rsquo;t available right now — this is a loading or
            connection problem, not an empty list. Reopen the row to retry.
          </span>
        </p>
      </Group>
    );
  }

  if (rollup.total === 0) {
    return (
      <Group label="Agreements" testId={testId}>
        <p className="detail-note" data-testid={`${testId}-empty`}>
          <span className="muted">{emptyMessage}</span>
        </p>
      </Group>
    );
  }

  return (
    <Group label={`Agreements (${rollup.total})`} testId={testId}>
      <table className="detail-table">
        <thead>
          <tr>
            <th>{show === 'customer' ? 'Customer' : 'Property'}</th>
            <th>Status</th>
            {/* RAC sits next to Rate on purpose: Jeff asked for "the Rate Access
                Codes and their corresponding rates", and the pairing is the whole
                point — a rate you cannot quote a code for is not usable. */}
            <th>RAC</th>
            <th>Type</th>
            <th>Term ends</th>
            <th className="right">Rate</th>
          </tr>
        </thead>
        <tbody>
          {rollup.shown.map((a) => (
            <tr key={a.id}>
              {/* Names the other side of the join, and opens the AGREEMENT —
                  not that side's own profile. The agreement is the actionable
                  thing from here, and it matches the Agreements table, where the
                  row's primary identifier is the link to its detail route.
                  Falls back to the customer id, which is never null, so the cell
                  is never empty and the id support pastes into tickets stays
                  reachable via the title. */}
              <td style={{ fontWeight: 600 }}>
                <Link
                  className="row-link"
                  data-testid={`agreement-rollup-open-${a.id}`}
                  title={show === 'customer' ? a.customerId : undefined}
                  to={`/hotel-supplier/agreements/${a.id}`}
                >
                  {show === 'customer'
                    ? a.customerName || a.customerId
                    : (a.propertyName ?? a.customerId)}
                </Link>
              </td>
              <td>
                <span className={statusBadge(a.status)}>
                  {statusLabel(a.status)}
                </span>
              </td>
              {/* Mono, because these get read out to a GDS desk character by
                  character. An em-dash here is a real signal: a live agreement
                  with no load code has not actually been loaded anywhere. */}
              <td className="mono">{racList(a) ?? '—'}</td>
              {/* The column that tells a consortium rate apart from one we
                  negotiated. As a number they are indistinguishable, and which
                  one it is decides whether there is anything left to negotiate. */}
              <td>{a.rateType ? statusLabel(a.rateType) : '—'}</td>
              <td>{fmtMaybeCalendarDate(a.termEnd) ?? '—'}</td>
              <td className="right">{rate(a)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* NOTES ROLLED UP FROM THE AGREEMENTS, at the property only.
          Stacie, 2026-08-31: "we need to make sure that we have agreement level
          notes and hotel level notes. And agreement level notes should show up
          when I click on the hotel" — then, asked whether it went both ways:
          "but not the other way around."

          One-way on purpose, and the asymmetry is the point. A note about one
          customer's negotiation ("they won't budge past 10%") is useful context
          for anyone else working that property. A property fact is NOT
          automatically true of every agreement on it, so it must not be pushed
          down.

          `show === 'property'` gates it: this component also renders on the
          Customers panel, where the rows ARE the customer's agreements and their
          own notes are already one click away. Rolling them up there would be
          repetition, not context.

          Attributed by customer, because an unattributed note about a rate is
          the kind of thing that gets applied to the wrong negotiation. */}
      {show === 'property' &&
        rollup.shown.some((a) => a.internalNote || a.hotelNote) && (
          <div
            data-testid={`${testId}-notes`}
            style={{ marginTop: 12, display: 'grid', gap: 8 }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                color: 'var(--text-muted)',
              }}
            >
              Notes from these agreements
            </div>
            {rollup.shown
              .filter((a) => a.internalNote || a.hotelNote)
              .map((a) => (
                <p className="detail-note" key={`notes-${a.id}`}>
                  <span className="detail-note-label">
                    {a.customerName || a.customerId}
                  </span>
                  {[a.hotelNote, a.internalNote].filter(Boolean).join(' · ')}
                </p>
              ))}
          </div>
        )}

      {/* The capped-list footer. Present ONLY when something was actually cut,
          and it names the number — a bare "more" leaves the reader unable to
          tell eight-of-nine from eight-of-eighty. */}
      {rollup.truncated > 0 && (
        <p className="detail-note" data-testid={`${testId}-truncated`}>
          <span className="muted">
            {moreHref ? (
              <>
                Showing {rollup.shown.length} of {rollup.total}.{' '}
                <Link to={moreHref}>See all {rollup.total}</Link>
              </>
            ) : (
              <>
                Showing {rollup.shown.length} of {rollup.total} — open an
                agreement to work it.
              </>
            )}
          </span>
        </p>
      )}
    </Group>
  );
}
